import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Col, Row } from "reactstrap";
import {
  getFormattedContactNumber,
  getModifiedObject,
  Toast,
  uploadImage,
} from "utility/helpers";
import { fetchAllRegions } from "services/services.congressman";
import { genericPost, genericPut } from "services/services.general";
import { fetchCandidates } from "services/services.official";
import ImgContainer from "components/common/img";
import defaultUserImage from "assets/img/default_user.png";
import { FILE_TYPE_IMAGE } from "constants/fileType";
import {
  FormContainer,
  FormHeaderSection,
  FormSection,
  HeaderText,
  ImageWrapper,
  UploadButton,
  UploadInput,
} from "assets/js/Styles";
import usePartylists from "hooks/usePartylists";
import DirectoryForm from "../Form";
import { POSITION_GOVERNOR } from "../constants";
import { validateOfficialDirectoryForm } from "../senator/utility";

const EditGovernor = () => {
  const location = useLocation();
  const gov = location.state?.governor || null;

  const [isLoading, setIsLoading] = useState(false);
  const [selectedPartylist, setSelectedPartylist] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [regionOptions, setRegionOptions] = useState([]);
  const [governorOptions, setGovernorOptions] = useState([]);
  const [selectedGovernor, setSelectedGovernor] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);

  const { options: partylists } = usePartylists();

  const [formFields, setFormFields] = useState({
    lastName: "",
    firstName: "",
    fullName: "",
    omegaId: "",
    region: "",
    contact: "",
    province: "",
    municipality: "",
    party: "",
    cos: "",
    cosNumber: "",
    photo: "",
    photoName: "",
    cosFirstName: "",
    cosLastName: "",
    name: "",
  });

  const navigate = useNavigate();

  const fetchGovernors = async () => {
    await fetchCandidates({
      url: "/election/nominations/?position_id=1&is_winner=true&page_size=100&is_active=true",
      callback: (data) => {
        if (data.results) {
          const rows = data.results;

          const governors = rows.map((governor) => ({
            ...governor,
            value: governor.candidate.id,
            // eslint-disable-next-line max-len
            label: `${governor.candidate.first_name} ${governor.candidate.last_name}`,
          }));

          if (gov) {
            const selectedGov = governors.find(
              (governor) => governor.candidate.id === gov.omegaId
            );
            setSelectedGovernor(selectedGov);
          }

          setGovernorOptions(governors);
        }

        setIsLoading(false);
      },
    });
  };

  const fetchRegion = () => {
    fetchAllRegions({
      callback: (data) => {
        if (data.length) {
          const options = data
            .filter((region) => region.name !== "Partylist")
            .map(({ acronym, id, omegaId }) => ({
              omegaId,
              label: acronym,
              value: id,
            }));

          setRegionOptions(options);

          const region = options?.find(
            (option) => option.value === gov?.regionId
          );

          if (region) {
            setSelectedRegion(region);
          } else {
            setFormFields({
              ...formFields,
              region: null,
            });
          }
        }
      },
    });
  };

  const setData = () => {
    const option = partylists.find((partylist) => partylist.id === gov.partyId);

    if (option) setSelectedPartylist(option);

    setIsLoading(false);
  };

  const setGovDetails = () => {
    const updatedFields = {
      ...formFields,
      lastName: gov.lastName,
      firstName: gov.firstName,
      fullName: gov.fullName,
      contact: getFormattedContactNumber(gov.contact),
      party: gov.partyId,
      region: selectedRegion,
      provinceId: gov.provinceId,
      municipalityId: gov.municipalityId,
      cos: gov.cosName,
      cosNumber: getFormattedContactNumber(gov.cosContact),
      photo: gov.photo || "",
      cosFirstName: gov.cosFirstName || gov.cosName,
      cosLastName: gov.cosLastName,
    };

    if (gov.photo) {
      const imgContainer = document.querySelector(".gov-img");
      imgContainer.src = gov.photo;
    }

    setFormFields(updatedFields);
  };

  const sendData = (formData) => {
    setIsLoading(true);

    if (gov) {
      genericPut({
        url: `/official/${gov.id}`,
        formData,
        callback: ({ isSuccess, msg }) => {
          setIsLoading(false);

          if (isSuccess) {
            navigate("/directory", {
              state: { view: POSITION_GOVERNOR },
            });

            Toast({
              type: 1,
              content: "Successfully edited governor details",
            });
          } else {
            Toast({
              type: 4,
              content: msg,
            });
          }
        },
      });
    } else {
      genericPost({
        url: "/official",
        formData,
        callback: ({ isSuccess, msg }) => {
          setIsLoading(false);

          if (isSuccess) {
            navigate("/directory", {
              state: { view: POSITION_GOVERNOR },
            });

            Toast({
              type: 1,
              content: "Successfully added governor.",
            });
          } else {
            Toast({
              type: 4,
              content: msg,
            });
          }
        },
      });
    }
  };

  const handleOnSubmit = async () => {
    const validationResult = await validateOfficialDirectoryForm(formFields);

    if (validationResult.valid) {
      const data = {
        // eslint-disable-next-line max-len
        fullName: `GOV. ${formFields.firstName} ${formFields.lastName}`,
        firstName: formFields.firstName,
        lastName: formFields.lastName,
        contact: formFields.contact?.slice(1),
        regionId: formFields.region.value,
        provinceId: formFields.province.value,
        cosName: `${formFields.cosFirstName} ${formFields.cosLastName}`,
        cosFirstName: formFields.cosFirstName,
        cosLastName: formFields.cosLastName,
        omegaId: formFields.omegaId,
        position: POSITION_GOVERNOR,
        ...(formFields.cosNumber ? { cosContact: formFields.cosNumber } : {}),
        ...(formFields.municipality.value !== "All Municipalities"
          ? { municipalityId: formFields.municipality.value }
          : {}),
        ...(selectedPartylist ? { partyId: selectedPartylist.value } : {}),
      };

      if (formFields.cosNumber) {
        data.cosContact = formFields.cosNumber?.slice(1);
      }

      let initialData = {};

      if (gov) {
        initialData = {
          firstName: gov.firstName,
          lastName: gov.lastName,
          fullName: gov.fullName,
          contact: gov.contact,
          regionId: gov.regionId,
          provinceId: gov.provinceId,
          municipalityId: gov.municipalityId,
          partyId: gov.partyId,
          cosName: gov.cosName,
          cosContact: gov.cosContact,
          cosFirstName: gov.cosFirstName,
          cosLastName: gov.cosLastName,
          photo: gov.photo,
          omegaId: formFields.omegaId,
          position: POSITION_GOVERNOR,
        };
      }

      setIsLoading(true);

      if (typeof formFields.photo !== "string") {
        const fileName = await uploadImage({
          photo: formFields.photo,
          photoName: formFields.photoName,
        });
        data.photo = fileName;
      }

      const formData = gov ? await getModifiedObject(initialData, data) : data;

      if (gov) {
        if (formFields.municipality.value === "All Municipalities") {
          formData.municipalityId = null;
        }
      }

      sendData(formData);
    } else {
      Toast({
        type: 4,
        content: validationResult.error,
      });
    }
  };

  const handleFileUpload = (evt) => {
    const file = evt.target.files[0];
    const reader = new FileReader();
    const fileTye = file.type.split("/")[0];

    if (fileTye === FILE_TYPE_IMAGE) {
      reader.onloadend = () => {
        const imgContainer = document.querySelector(".gov-img");

        const fileName = gov
          ? `${
              gov.id
            }-${formFields.lastName.toLocaleLowerCase()}-${Date.now()}.${
              file.type.split("/")[1]
            }`
          : `${formFields.lastName.toLocaleLowerCase()}-${Date.now()}.${
              file.type.split("/")[1]
            }`;

        setFormFields({
          ...formFields,
          photo: file,
          photoName: fileName,
        });

        imgContainer.src = reader.result;
      };

      reader.readAsDataURL(file);
    } else {
      Toast({
        type: 4,
        content: "File uploaded must be an image.",
      });
    }
  };

  useEffect(() => {
    setIsLoading(true);
    //  fetch all district, partylist, region
    fetchRegion();
    fetchGovernors();
  }, []);

  useEffect(() => {
    if (gov !== null) {
      setData();
      setGovDetails();
      setIsEdit(true);
    }
  }, [partylists, gov]);

  return (
    <FormContainer>
      <Col className="container">
        <FormHeaderSection>
          <HeaderText>{!gov ? "Add Governor" : "Governor Details"}</HeaderText>
        </FormHeaderSection>

        <FormSection>
          <Row>
            <Col
              xs="12"
              sm="6"
              lg="4"
            >
              <ImageWrapper>
                <ImgContainer
                  alt="gov-img"
                  className="gov-img"
                  src={defaultUserImage}
                  width="196px"
                  height="196px"
                />

                <UploadButton htmlFor="file-upload">
                  {"Upload Photo"}
                  <UploadInput
                    id="file-upload"
                    type="file"
                    onChange={handleFileUpload}
                  />
                </UploadButton>
              </ImageWrapper>
            </Col>
            <Col
              xs="12"
              sm="6"
              lg="8"
            >
              <DirectoryForm
                handleOnSubmit={handleOnSubmit}
                formFields={formFields}
                setFormFields={setFormFields}
                governorOptions={governorOptions}
                selectedGovernor={selectedGovernor}
                setSelectedGovernor={setSelectedGovernor}
                selectedRegion={selectedRegion}
                setSelectedRegion={setSelectedRegion}
                regionOptions={regionOptions}
                partylists={partylists}
                selectedPartylist={selectedPartylist}
                setSelectedPartylist={setSelectedPartylist}
                isLoading={isLoading}
                isEdit={isEdit}
                position={POSITION_GOVERNOR}
              />
            </Col>
          </Row>
        </FormSection>
      </Col>
    </FormContainer>
  );
};

export default EditGovernor;
